import Thumbnail from 'components/Thumbnail'
import { without } from 'lodash'
import { useQueryErrorHandler } from 'hooks'
import { useCollectiveContext } from 'context/CollectiveContext'
import { useState } from 'react'
import {
  useBatchProposalsMutation,
  CollectiveProposal,
  CollectiveProposalStatus,
  CollectiveProposalType
} from 'graphql/generated'
import useToasts from 'hooks/useToasts'
import { parseWeb3ErrorMessage, TTxUiState } from 'lib/collectives/helpers'
import Web3ActionModal from 'components/Collectives/modals/Web3ActionModal'
import MembershipRequiredModal from './MembershipRequiredModal'
import { faCheckCircle, faCircle } from '@fortawesome/pro-regular-svg-icons'
import { useListCollectiveProposals } from 'hooks/collectives/useListCollectiveProposals'
import ProposalStatusBadge from '../CollectiveProposals/ProposalStatusBadge'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { humanTime } from 'lib/datetime'
import { useRouter } from 'next/router'

export function BatchProposalsModal({ onClose }: { onClose?: Callback }) {
  const { demoMode, collective } = useCollectiveContext()

  const { addToast } = useToasts()

  const onError = useQueryErrorHandler(`Failed to batch proposals`)
  const [batchProposals, { loading: creating }] = useBatchProposalsMutation({
    onError
  })
  const { proposals } = useListCollectiveProposals(collective.id, 500)
  const availableProposals =
    proposals?.edges
      .map(x => x.node)
      .filter(x => x.type === CollectiveProposalType.COMMANDS)
      .filter(x => x.status === CollectiveProposalStatus.SUCCEEDED)
      .filter(x => !x.isBatch)
      .filter(x => !x.isPartOfBatch) || []
  const [proposalIds, setProposalIds] = useState<string[]>([])
  const onProposalClick = (proposal: Pick<CollectiveProposal, 'id'>) => {
    setProposalIds(curr =>
      curr.includes(proposal.id)
        ? without(curr, proposal.id)
        : [...curr, proposal.id]
    )
  }

  const [state, setState] = useState<Maybe<TTxUiState>>(null)
  const canSubmit = proposalIds.length > 0 && !creating && !demoMode
  const { push } = useRouter()

  const doBatch = async () => {
    if (!canSubmit) {
      return
    }

    try {
      setState(TTxUiState.WAITING_FOR_NETWORK)

      const resp = await batchProposals({
        variables: {
          proposalIds
        }
      })
      const batchProp = resp.data?.batchProposals
      if (!batchProp) {
        throw new Error(`Failed to batch proposals`)
      }

      push(batchProp.publicUrl)

      setState(TTxUiState.TX_SUCCESS)
    } catch (err) {
      addToast(parseWeb3ErrorMessage(err), {
        appearance: 'warning'
      })
      setState(null)
    }
  }

  return (
    <MembershipRequiredModal
      collective={collective}
      requiredRole={'privileged'}
      onClose={onClose}
    >
      <Web3ActionModal
        title={'Batch Proposals'}
        state={state}
        onConfirm={doBatch}
        onClose={onClose}
        disableConfirm={!canSubmit}
        labels={{
          [TTxUiState.TX_SUCCESS]: `You've successfully batched ${proposalIds.length} proposals`
        }}
      >
        <div className="flex flex-col">
          <p className="font-sans text-[15px] font-light text-gray-700 pb-[10px]">
            {`Select the proposals you'd like to batch. Once batched, you'll only
            need to sign once to execute them all. This can help save on gas
            fees.`}
          </p>

          <div className="max-h-[300px] overflow-auto bg-gray-100 p-[5px] rounded-[10px]">
            {availableProposals.map(proposal => {
              const isBotProposal = proposal.proposedBy.user.platformUser?.isBot

              return (
                <div
                  key={`proposal-${proposal.id}`}
                  onClick={() => onProposalClick(proposal)}
                  className="flex flex-row items-center cursor-pointer py-[15px] px-[10px] rounded-[10px] mb-[10px] hover:bg-white"
                >
                  <div className="flex justify-center w-[30px]">
                    <FontAwesomeIcon
                      icon={
                        proposalIds.includes(proposal.id)
                          ? faCheckCircle
                          : faCircle
                      }
                    />
                  </div>

                  <div className="flex justify-center w-[70px]">
                    {proposal.proposedBy.user.platformUser && !isBotProposal ? (
                      <Thumbnail
                        src={proposal.proposedBy.user.platformUser.thumb}
                        initials={proposal.proposedBy.user.platformUser.displayName?.substring(
                          0
                        )}
                        className="h-12 w-12 rounded-full overflow-hidden"
                      />
                    ) : (
                      <div />
                    )}
                  </div>

                  <div className="flex flex-1 flex-col">
                    <p className="font-sans text-sm font-normal text-gray-500">
                      {proposal.title}
                    </p>

                    <p className="font-sans text-sm font-light text-gray-500">
                      {isBotProposal
                        ? `\u00b7 ${humanTime(proposal.createdAt)}`
                        : `Proposed by: ${
                            proposal.proposedBy.user.platformUser
                              ?.displayName || proposal.proposedBy.user.address
                          } \u00b7 ${humanTime(proposal.createdAt)}`}
                    </p>
                  </div>

                  <ProposalStatusBadge proposal={proposal} />
                </div>
              )
            })}
          </div>
        </div>
      </Web3ActionModal>
    </MembershipRequiredModal>
  )
}
